<template>
  <div>
    <v-container fluid grid-list-sm>
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon>mdi-information</v-icon>
          <v-toolbar-title>{{ labels.policyInfo }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs6 md6 lg6>
              <cal-picker
                v-model="claimForm.notDate"
                v-validate="notDateValidation"
                :label="labels.notDate"
                :data-vv-as="labels.notDate"
                data-vv-name="notDate"
                :error-messages="errors.collect('notDate')"
                :min="minNotDate"
                :max="maxNotDate"
              ></cal-picker>
            </v-flex>
            <v-flex xs6 md6 lg6>
              <cal-picker
                v-model="claimForm.tranDate"
                v-validate="tranDateValidation"
                :label="labels.tranDate"
                :data-vv-as="labels.tranDate"
                data-vv-name="claimForm.tranDate"
                :error-messages="errors.collect('claimForm.tranDate')"
                data-vv-scope="tranDateRange"
              ></cal-picker>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs6 md6 lg6>
              <cal-picker
                v-model="claimForm.lossDate"
                v-validate="lossDateValidation"
                :label="labels.lossDate"
                :data-vv-as="labels.lossDate"
                data-vv-name="lossDate"
                :error-messages="errors.collect('lossDate')"
                :hint="getLossDateHint(claimForm.lossDate)"
                :persistent-hint="true"
                :class="getLossDateClass()"
                :max="maxStatDate"
              ></cal-picker>
            </v-flex>
            <v-flex xs6 md6 lg6>
              <time-picker
                v-model="claimForm.lossTime"
                v-validate="'required'"
                :label="labels.lossTime"
                :data-vv-as="labels.lossTime"
                data-vv-name="lossTime"
                :error-messages="errors.collect('lossTime')"
              ></time-picker>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex lg6 md12 xs12>
              <v-text-field
                v-model="claimForm.polNo"
                :label="labels.polNo"
                data-vv-name="polNo"
                :data-vv-as="labels.polNo"
                :error-messages="errors.collect('polNo')"
                @change="getPolicy(claimForm.polNo)"
                :hint="hasClaimHint()"
                :persistent-hint="true"
                :class="getHasClaimHintClass()"
              >
                <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                  <v-icon color="info" @click="doShowPolicys" id="getPoliciesBtn">mdi-magnify</v-icon>
                </v-slide-x-reverse-transition>
              </v-text-field>
            </v-flex>
            <v-flex lg2 md4 xs12>
              <v-autocomplete
                v-model="claimForm.riskGrpNo"
                v-validate="'required'"
                :items="riskGrpNoList"
                :label="labels.riskGrpNo"
                item-text="label"
                item-value="value"
                data-vv-name="riskGrpNo"
                :data-vv-as="labels.riskGrpNo"
                :error-messages="errors.collect('riskGrpNo')"
                :disabled="riskGrpNoList.length < 2"
                :persistent-hint="riskGrpNoList.length < 1"
                :hint="riskNoList.length < 1 ? 'Please select a Risk Group No.' : ''"
                :loading="riskLoading"
              ></v-autocomplete>
            </v-flex>
            <v-flex lg4 md8 xs12>
              <v-autocomplete
                v-model="claimForm.riskNo"
                v-validate="'required'"
                :items="riskNoList"
                :label="labels.riskNo"
                item-text="label"
                item-value="value"
                data-vv-name="riskNo"
                :data-vv-as="labels.riskNo"
                :error-messages="errors.collect('riskNo')"
                :disabled="riskNoList.length < 2"
                :persistent-hint="riskNoList.length < 1"
                :hint="riskNoList.length < 1 ? 'Please select a Risk No.' : ''"
                @change="getExtraRiskInfo"
                :loading="riskLoading"
              ></v-autocomplete>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon>mdi-account</v-icon>
          <v-toolbar-title>{{ labels.claimInfo }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <v-layout row wrap>
              <v-flex xs12 md6 lg6 v-if="claimForm.clmNoAe">
                <v-checkbox left color="primary" v-model="claimForm.clmNoAe" :label="labels.clmNoAe" readonly></v-checkbox>
              </v-flex>
              <v-flex xs12 md6 lg6 v-if="!claimForm.clmNoAe">
                <v-text-field
                  v-model="claimForm.clmNo"
                  :label="labels.clmNo"
                  :data-vv-as="labels.clmNo"
                  v-validate="claimForm.clmNoAe ? '' : 'required|name|max:12'"
                  data-vv-name="clmNo"
                  :error-messages="errors.collect('clmNo')"
                ></v-text-field>
              </v-flex>
              <v-flex xs12 md6 lg6 />
              <v-flex xs12 md6 lg6>
                <v-autocomplete
                  :items="claimStatList"
                  v-validate="'required'"
                  v-model="claimForm.clmStat"
                  :label="labels.clmStat"
                  item-text="label"
                  item-value="value"
                  data-vv-name="clmStat"
                  :error-messages="errors.collect('clmStat')"
                  :data-vv-as="labels.clmStat"
                  @change="doCauseList(claimForm.clmStat)"
                ></v-autocomplete>
              </v-flex>
              <v-flex xs12 md6 lg6>
                <cal-picker
                  v-model="claimForm.statDate"
                  v-validate="statDateValidation"
                  :label="labels.statDate"
                  :data-vv-as="labels.statDate"
                  data-vv-name="statDate"
                  :error-messages="errors.collect('statDate')"
                  :min="minStatDate"
                  :max="maxStatDate"
                ></cal-picker>
              </v-flex>
              <v-flex xs12 md6 lg6>
                <v-autocomplete
                  :items="causeStatList"
                  v-validate="'required'"
                  v-model="claimForm.cseStat"
                  :label="labels.cseStat"
                  item-text="label"
                  item-value="value"
                  data-vv-name="cseStat"
                  :data-vv-as="labels.cseStat"
                  :error-messages="errors.collect('cseStat')"
                  :disabled="claimForm.clmStat === null || typeof claimForm.clmStat === 'undefined'"
                  :persistent-hint="claimForm.clmStat === null || typeof claimForm.clmStat === 'undefined'"
                  :hint="claimForm.clmStat === null || typeof claimForm.clmStat === 'undefined' ? 'Please select a Claim Status' : ''"
                ></v-autocomplete>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions right>
          <v-btn color="error" @click.native="doExit">
            <span>Exit</span>
            <v-icon right>mdi-exit-to-app</v-icon>
          </v-btn>
          <v-btn color="primary" type="submit" @click.prevent="doProceed" id="createClaimBtn">
            Register Claim
            <v-icon right>mdi-plus</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
      <v-dialog v-model="showPolicys" width="1280px" persistent>
        <v-card>
          <v-card-title class="secondary lighten-4">
            <span class="headline">Policy</span>
            <v-spacer></v-spacer>
            <v-btn text icon @click="closePolicy()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-flex xs12 v-if="showPolicys">
              <message :messages="messages"></message>
            </v-flex>
            <v-flex xs12>
              <form data-vv-scope="queryPolicyFilterForm">
                <v-expansion-panels>
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div><v-icon>mdi-filter-variant</v-icon>Filters</div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-card>
                        <v-card-text>
                          <v-container fluid grid-list-sm>
                            <v-layout row wrap>
                              <v-flex lg6 xs12>
                                <v-text-field
                                  data-vv-scope="queryPolicyFilterForm"
                                  v-model="filters.polNo"
                                  :label="filtersLabel.polNo"
                                  :error-messages="errors.collect('queryPolicyFilterForm.filterPolNo')"
                                  data-vv-name="filterPolNo"
                                  :data-vv-as="filtersLabel.polNo"
                                  v-validate="'entry|max:16'"
                                ></v-text-field>
                              </v-flex>
                              <v-flex lg6 xs12>
                                <v-text-field
                                  data-vv-scope="queryPolicyFilterForm"
                                  v-model="filters.insdName1"
                                  :label="filtersLabel.insdName1"
                                  :error-messages="errors.collect('queryPolicyFilterForm.filterInsdName1')"
                                  data-vv-name="filterInsdName1"
                                  :data-vv-as="filtersLabel.insdName1"
                                  v-validate="'name|max:60'"
                                ></v-text-field>
                              </v-flex>
                              <v-flex lg6 xs12>
                                <cal-picker
                                  data-vv-scope="queryPolicyFilterForm"
                                  v-model="filters.effDate"
                                  :label="filtersLabel.effDate"
                                  :data-vv-as="filtersLabel.effDate"
                                  data-vv-name="filterEffDate"
                                  :error-messages="errors.collect('queryPolicyFilterForm.filterEffDate')"
                                ></cal-picker>
                              </v-flex>
                            </v-layout>
                          </v-container>
                        </v-card-text>
                        <v-card-actions right>
                          <v-spacer></v-spacer>
                          <v-btn color="red darken-1" text @click.native="doClearFilters">Clear Filters</v-btn>
                          <v-btn color="primary" text @click.prevent="doFilters">Apply Filters</v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </form>
            </v-flex>
            <v-flex xs12 class="mt-2">
              <v-data-table
                class="elevation-1"
                v-bind:headers="headers"
                v-bind:items="items"
                :server-items-length="totalItems"
                :footer-props="{ 'items-per-page-options': itemsPerPage }"
                v-bind:options.sync="pagination"
                item-key="x"
                :loading="loading"
                :no-data-text="noDataText"
              >
                <template v-slot:[`item.actions`]="row">
                  <td style="text-align: center">
                    <v-btn icon color="primary" text type="submit" @click.prevent="doSelectPolicy(row.item)">
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                  </td>
                </template>
              </v-data-table>
            </v-flex>
          </v-card-text>
        </v-card>
      </v-dialog>
      <v-dialog v-model="showProcNotif" width="640px" eager persistent>
        <ProcessNotification ref="procNotifDlg" :procNotifData="procNotifData" @fetch="fetchPN" @proceed="proceedPN"></ProcessNotification>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import clone from 'clone'
import MessageMixin from '@/mixins/message'
import { expandURI, filterQuery } from '@/utils/ajax'
import { CLAIMS_LIFECYCLE, INIT_CLAIM_POLICY, CLAIMS_CLAIMDETAIL_RESET, CLAIMS_PROCESS_NOTIF_RESET, CLAIMS_PROCESS_NOTIF_SET } from '@/store/types'
import * as TableConst from '@/mixins/table'
import { formatDisplayDate, formatDisplayDateTime2, removeTime, formatDisplayTime } from '@/utils/date'
// import { isEmptyOrWhiteSpace } from '@/utils/index'
import isAfter from 'date-fns/isAfter'
import ProcessNotification from '@/components/core/common/dialog/ProcessNotification'
import Router from '@/router'
import { parseISO } from 'date-fns'

export default {
  data() {
    return {
      showPolicys: false,
      labels: {
        clmNoAe: 'Auto Generate Claim No.',
        policyInfo: 'General Information',
        claimInfo: 'Claim Information',
        insuredInfo: 'Insured Information',
        claimNew: 'Register Claim',
        clmNo: 'Claim No.*',
        clmStat: 'Claim Status*',
        polNo: 'Policy No.*',
        statDate: 'Date of Status*',
        cseStat: 'Cause of Status*',
        lossDate: 'Date of Loss / Accident*',
        lossTime: 'Time of Loss*',
        notDate: 'Notification Date*',
        riskGrpNo: 'Risk Group No.*',
        riskNo: 'Risk No.*',
        tranDate: 'Transaction Date*'
      },
      commonForm: {
        polNo: undefined,
        insdName1: undefined,
        icNo: undefined,
        vehRegNo: undefined,
        vesNo: undefined,
        comDate: undefined,
        expDate: undefined,
        tranDate: undefined,
        renCnt: undefined,
        endtCnt: undefined
      },
      claimForm: {
        clmNo: null,
        clmNoAe: undefined,
        clmStat: undefined,
        riskGrpNo: 1,
        riskNo: 0,
        statDate: undefined,
        cseStat: undefined,
        lossDate: undefined,
        lossTime: undefined,
        notDate: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        tranDate: undefined
      },
      controlFlag: {
        mtr: false,
        hull: false,
        fire: false,
        cargo: false,
        med: false,
        misc: false,
        pa: false
      },
      filtersLabel: {
        polNo: 'Policy No.',
        insdName1: 'Insured Name',
        effDate: 'Effective Date',
        search: 'Search'
      },
      filters: {
        polNo: undefined,
        insdName1: undefined,
        effDate: undefined,
        search: undefined
      },
      // Dialog config
      headers: [
        { text: 'Policy No.', align: 'left', value: 'polNo' },
        { text: 'RenCnt', align: 'left', value: 'renCnt' },
        { text: 'EndtCnt', align: 'left', value: 'endtCnt' },
        { text: 'Insured Ref', align: 'left', value: 'insdRefNo' },
        { text: 'Insured Name', value: 'insdName1' },
        { text: 'NRIC / Passport', value: 'icNo' },
        { text: 'Commencement Date', value: 'comDate' },
        { text: 'Expired Date', value: 'expDate' },
        { text: 'Action', value: 'actions', align: 'center', sortable: false }
      ],
      totalItems: 0,
      riskGrpNoList: [{ label: '1', value: 1 }],
      riskNoList: [{ label: '0', value: 0 }],
      claimStatList: [],
      causeStatList: [],
      defCauseStatList: [],
      lossDateWarning: false,
      hasClaimWarning: false,
      riskLoading: false,
      showProcNotif: false,
      pagination: {
        sortDesc: [false],
        page: 1
      },
      tranDateFrom: undefined,
      tranDateTo: undefined
    }
  },
  computed: {
    minLossDate() {
      if (this.claimForm.comDate !== undefined) {
        let result
        result = this.claimForm.comDate
        return result
      }
      return undefined
    },
    maxLossDate() {
      if (this.claimForm.expDate !== undefined) {
        let result
        result = this.claimForm.expDate
        return result
      }
      return undefined
    },
    minNotDate() {
      if (this.claimForm.lossDate !== undefined) {
        let result
        result = this.claimForm.lossDate
        return result
      }
      return undefined
    },
    maxNotDate() {
      let result = new Date()
      return result
    },
    minStatDate() {
      if (this.claimForm.lossDate !== undefined) {
        let result
        result = this.claimForm.lossDate
        return result
      }
      return undefined
    },
    maxStatDate() {
      let result = new Date()
      return result
    },
    lossDateValidation() {
      const result =
        'required|claims_lossDate_comDate:' + this.commonForm.comDate + '|claims_lossDate_expDate:' + this.commonForm.expDate + '|claims_Date_today'
      return result
    },
    notDateValidation() {
      const result = 'required|claims_Date_lossDate:' + this.claimForm.lossDate + '|claims_Date_today'
      return result
    },
    tranDateValidation() {
      // return 'required'
      return 'required|tranDateRangeValidation:' + this.tranDateFrom + ',' + this.tranDateTo + ',' + this.claimForm.tranDate
    },
    statDateValidation() {
      const result = 'required|claims_Date_lossDate:' + this.claimForm.lossDate + '|claims_Date_today'
      return result
    },
    procNotifShow() {
      return this.$store.state.claims.procNotif.show
    },
    procNotifData() {
      return this.$store.state.claims.procNotif
    }
  },
  watch: {
    // For pagination, we dont want to watch all the pagination property using deep (shown in vuetify eg)
    // This way the application will load multiple times at the start (when setting totalElements etc)
    procNotifShow: async function (value) {
      if (value) {
        const apiURI = this.$store.state.claims.claimRegister.menu.fetchProcNotifParam
        if (apiURI) {
          try {
            this.showProcNotif = false
            const expand = {
              procId: '201'
            }
            const result = await this.$http.get(expandURI(apiURI, expand))
            if (result.data) {
              this.doFetchPN()
            }
          } catch (err) {
            this.showProcNotif = false
            this.$store.commit(CLAIMS_PROCESS_NOTIF_RESET)
            Router.push({ name: 'claimHeader' })
          }
        }
      }
    },
    pagination: function (value) {
      this.fetchData(false)
    },
    'claimForm.tranDate': function (value) {
      this.$validator.validateAll('tranDateRange')
    }
  },
  methods: {
    closePolicy() {
      this.resetMessages()
      this.showPolicys = false
    },
    doExit() {
      this.$router.push({ name: 'mngClaim' })
    },
    doProceed() {
      if (this.$validator.errors.has('polNo')) {
        this.promptFieldCheck()
      } else {
        this.resetMessages()
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.doSave()
          } else {
            this.promptFieldCheck()
          }
        })
      }
    },
    async doSave() {
      await Promise.all([this.$store.dispatch('createClaim', this.claimForm)])
    },
    doShowPolicys() {
      this.$validator.validate('lossDate', this.claimForm.lossDate).then((result) => {
        if (result) {
          this.showPolicys = true
          this.$set(this, 'items', [])
          this.fetchData()
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doSelectPolicy(policy) {
      this.claimForm.polNo = policy.polNo
      this.claimForm.renCnt = policy.renCnt
      this.claimForm.endtCnt = policy.endtCnt
      this.getPolicy(this.claimForm, true)
      this.showPolicys = false
    },
    async getPolicy(policyId) {
      this.$validator.errors.remove('polNo')
      const apiUrl = this.$store.state.claims.claimRegister.menu.getPolicy
      if (apiUrl) {
        try {
          this.riskLoading = true
          const expand = {
            polNo: typeof policyId.polNo === 'undefined' ? this.claimForm.polNo : policyId.polNo,
            renCnt: policyId.renCnt,
            endtCnt: policyId.endtCnt,
            lossDate: this.claimForm.lossDate
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result.data
          // reset Risk Group
          this.claimForm.riskNo = 0
          this.claimForm.riskGrpNo = 1
          // this.riskGrpNoList = []
          // this.riskNoList = []
          if (data) {
            this.claimForm.clmNoAe = data.defClaimNoAE
            this.commonForm.polNo = data.policy.polNo
            this.commonForm.insdName1 = data.policy.insdName1
            this.commonForm.icNo = data.policy.icNo
            this.commonForm.comDate = data.policy.comDate
            this.commonForm.expDate = data.policy.expDate
            this.commonForm.tranDate = data.policy.tranDate
            this.commonForm.endtCnt = data.policy.endtCnt
            this.commonForm.renCnt = data.policy.renCnt
            if (data.policy.permittedRisk.length > 0) {
              this.riskGrpNoList = []
              this.riskNoList = []
              data.policy.permittedRisk.map((element) => {
                const riskGrp = {
                  label: element.riskGrpNo,
                  value: element.riskGrpNo
                }
                const riskNo = {
                  label: element.riskNo + ' - ' + element.busCls + ' - ' + element.busClsDesc,
                  value: element.riskNo
                }
                this.riskGrpNoList.push(riskGrp)
                this.riskNoList.push(riskNo)
              })
            } else {
              this.riskGrpNoList = [{ label: '1', value: 1 }]
              this.riskNoList = [{ label: '0', value: 0 }]
            }
            if (this.riskGrpNoList.length > 0) {
              this.claimForm.riskGrpNo = this.riskGrpNoList[0].value
            } else {
              this.claimForm.riskGrpNo = 1
            }
            if (this.riskNoList.length > 0) {
              this.claimForm.riskNo = this.riskNoList[0].value
            } else {
              this.claimForm.riskNo = 0
            }
            this.$store.commit(INIT_CLAIM_POLICY, this.commonForm)
            this.hasClaim(this.commonForm.polNo)
          }
          this.riskLoading = false
        } catch (error) {
          const { data } = error.response
          if (data) {
            this.$validator.errors.add({
              field: 'polNo',
              msg: data.messages[0].message
            })
            // this.message(data)
          }
          this.riskLoading = false
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doClearFilters() {
      this.filters.polNo = null
      this.filters.effDate = null
      this.filters.insdName1 = null
      this.fetchData(false)
    },
    doFilters() {
      this.resetMessages()
      this.$validator.validateAll('queryPolicyFilterForm').then((result) => {
        if (result) {
          // Reset to page 1
          this.pagination.page = 1
          this.fetchData(false)
        }
      })
    },
    // Find Policy in Policy Master
    async fetchData() {
      const apiUrl = this.$store.state.claims.claimRegister.menu.polMasterList
      if (apiUrl) {
        try {
          this.search = null
          this.loading = true
          const { page, itemsPerPage } = this.pagination
          const expand = {
            page: page - 1,
            size: itemsPerPage,
            lossDate: formatDisplayDateTime2(this.claimForm.lossDate)
          }
          let massageFilters = clone(this.filters)
          if (massageFilters.effDate) {
            massageFilters.effDate = formatDisplayDateTime2(removeTime(massageFilters.effDate))
          }
          const result = await this.$http.get(
            filterQuery(expandURI(apiUrl, expand), massageFilters, this.pagination.sortBy, this.pagination.sortDesc)
          )
          if (result.data) {
            this.totalItems = result.data.totalElements
            const { data } = result.data
            if (data) {
              // Format the data here
              data.map((row) => {
                if (row.comDate) {
                  row.comDate = this.formatDate(row.comDate)
                }
                if (row.expDate) {
                  row.expDate = this.formatDate(row.expDate)
                }
              })
              this.$set(this, 'items', data)
            } else {
              this.$set(this, 'items', [])
            }
          }
          this.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getExtraRiskInfo() {
      const apiUrl = this.$store.state.claims.claimRegister.menu.getExtraRiskInfo
      if (apiUrl) {
        const expand = {
          polNo: this.commonForm.polNo,
          renCnt: this.commonForm.renCnt,
          endtCnt: this.commonForm.endtCnt,
          riskGrpNo: this.claimForm.riskGrpNo,
          riskNo: this.claimForm.riskGrpNo
        }
        try {
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result.data
          if (data) {
            this.commonForm.vehRegNo = data.policyRisk.vehRegNo
            this.commonForm.vesNo = data.policyRisk.vesNo
          }
          this.$store.commit(INIT_CLAIM_POLICY, this.commonForm)
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getClaimStats() {
      const apiURI = this.$store.state.claims.claimRegister.menu.getClaimStats
      if (apiURI) {
        try {
          const result = await this.$http.get(apiURI)
          const { data } = result.data
          this.claimStatList = []
          this.defCauseStatList = []
          if (data) {
            data.map((item) => {
              const claimStat = {
                value: item.clmStat,
                label: item.clmStatDesc
              }
              item.clmStatCauseList.map((element) => {
                const causeStat = {
                  value: element.value,
                  secondaryValue: element.secondaryValue,
                  label: element.label
                }
                this.defCauseStatList.push(causeStat)
              })
              this.claimStatList.push(claimStat)
            })
          }
          const { resultMap } = result.data
          if (resultMap) {
            this.claimForm.clmNoAe = resultMap.clmNoAE === 'true'
          } else {
            // do nothing
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getTranDateRange() {
      const apiURI = this.$store.state.claims.claimRegister.menu.getTranDateRange
      if (apiURI) {
        const result = await this.$http.get(apiURI)
        const { data } = result.data
        if (data) {
          if (data.tranDateRange !== null) {
            this.tranDateFrom = data.tranDateRange.fromDate
            this.tranDateTo = data.tranDateRange.toDate
          }
        }
      }
    },
    doCauseList(value) {
      this.causeStatList = []
      this.defCauseStatList.map((item) => {
        if (item.secondaryValue === value) {
          const causeStat = {
            label: item.label,
            value: item.value
          }
          this.causeStatList.push(causeStat)
        }
      })
    },
    formatDate(date) {
      return formatDisplayDate(date)
    },
    getLossDateHint(lossDate) {
      if (lossDate) {
        if (this.claimForm.comDate && this.claimForm.expDate) {
          if (
            isAfter(removeTime(this.claimForm.comDate), removeTime(lossDate)) ||
            isAfter(removeTime(lossDate), removeTime(this.claimForm.expDate))
          ) {
            this.lossDateWarning = true
            return 'This Loss Date is not covered.'
          } else {
            this.lossDateWarning = false
            return ''
          }
        }
      } else {
        this.lossDateWarning = false
        return ''
      }
    },
    getLossDateClass() {
      if (this.lossDateWarning) {
        return 'lossDateWarning'
      } else {
        return ''
      }
    },
    getHasClaimHintClass() {
      if (this.hasClaimWarning) {
        return 'hasClaimWarning'
      } else {
        return ''
      }
    },
    hasClaimHint() {
      if (this.riskNoList.length < 1) {
        return 'Please select a Policy No.'
      } else if (this.hasClaimWarning) {
        return 'A Claim has been filed for this Policy No.'
      } else {
        return ''
      }
    },
    hasRiskHint() {
      if (this.claimForm.polNo) {
        return ''
      } else return 'Please select a Risk No.'
    },
    async hasClaim(polNo) {
      const apiURI = this.$store.state.claims.claimRegister.menu.hasClaim
      if (apiURI) {
        if (polNo !== undefined && polNo !== null && polNo !== '') {
          const expand = {
            polNo: polNo
          }
          const result = await this.$http.get(expandURI(apiURI, expand))
          if (result.data) {
            this.hasClaimWarning = true
          } else {
            this.hasClaimWarning = false
          }
        } else {
          this.hasClaimWarning = false
        }
      } else {
        this.hasClaimWarning = false
      }
    },
    async doFetchPN() {
      this.$refs.procNotifDlg.fetchProcNotif()
    },
    async fetchPN() {
      const apiUrl = this.$store.state.claims.claimRegister.menu.fetchProcNotif
      if (apiUrl) {
        try {
          const req = this.procNotifData
          const expand = {
            notifType: req.notifType,
            clmNo: req.clmNo
          }
          const result = await this.$http.post(apiUrl, expand)
          if (result.data) {
            const { data } = result
            const record = {
              show: true,
              notifType: 'NEWCLM',
              clmNo: data.clmNo,
              emailInsured: data.emailInsured,
              emailAddressInsured: data.emailAddressInsured,
              smsInsured: data.smsInsured,
              smsNumberInsured: data.smsNumberInsured,
              emailAgent: data.emailAgent,
              emailAddressAgent: data.emailAddressAgent,
              smsAgent: data.smsAgent,
              smsNumberAgent: data.smsNumberAgent
            }
            this.$store.commit(CLAIMS_PROCESS_NOTIF_SET, record)
            this.$refs.procNotifDlg.refreshForm()
            this.showProcNotif = true
          } else {
            this.$store.commit(CLAIMS_PROCESS_NOTIF_RESET)
            Router.push({ name: 'claimHeader' })
          }
        } catch (error) {
          const { data } = error.response
          if (data) {
            this.message(data)
          }
        }
      }
    },
    async proceedPN(req) {
      const apiUrl = this.$store.state.claims.claimRegister.menu.processProcNotif
      if (apiUrl) {
        try {
          const form = {
            notifType: 'NEWCLM',
            clmNo: this.procNotifData.clmNo,
            emailInsured: req.emailInsured,
            emailAddressInsured: req.emailAddressInsured,
            smsInsured: req.smsInsured,
            smsNumberInsured: req.smsNumberInsured,
            emailAgent: req.emailAgent,
            emailAddressAgent: req.emailAddressAgent,
            smsAgent: req.smsAgent,
            smsNumberAgent: req.smsNumberAgent
          }
          this.$store.commit(CLAIMS_PROCESS_NOTIF_RESET)
          await this.$http.post(apiUrl, form)
        } catch (error) {
          const { data } = error.response
          if (data) {
            this.message(data)
          }
        }
      }
      Router.push({ name: 'claimHeader' })
    },
    parseDateISO(value) {
      if (value) {
        return parseISO(value)
      }
    }
  },
  mounted() {
    this.$store.commit(CLAIMS_PROCESS_NOTIF_RESET)
    // Start of claims to reset claim state
    this.$store.commit(CLAIMS_CLAIMDETAIL_RESET)
    Promise.all([this.getClaimStats()]).then((result) => {
      this.claimForm.clmStat = 'O'
      Promise.all([this.doCauseList(this.claimForm.clmStat)]).then((result) => {
        this.claimForm.cseStat = 'N'
      })
    })
    this.claimForm.statDate = new Date()
    this.claimForm.statDate.setHours(0)
    this.claimForm.statDate.setMinutes(0)
    this.claimForm.lossTime = formatDisplayTime(this.claimForm.statDate)
    this.claimForm.tranDate = new Date()
    this.getTranDateRange()
  },
  beforeMount() {
    // Check claim lifecycle (might be using back link or url)
    if (this.$store.state.claims.claimDetail.claimLifeCycle !== 1) {
      this.$store.commit(CLAIMS_LIFECYCLE, 1)
    }
  },
  created() {
    // Local validator
    this.$validator.extend('claims_lossDate_comDate', {
      getMessage(field, args) {
        return `The ${field} is not covered.`
      },
      validate(value, args) {
        if (value) {
          if (args === void 0) args = []
          var comDate = args[0]
          if (comDate) {
            return !isAfter(removeTime(comDate), removeTime(value))
          } else return true
        } else return true
      }
    })
    this.$validator.extend('claims_lossDate_expDate', {
      getMessage(field, args) {
        return `The ${field} is not covered.`
      },
      validate(value, args) {
        if (value) {
          if (args === void 0) args = []
          var expDate = args[0]
          if (expDate) {
            return !isAfter(removeTime(value), removeTime(expDate))
          } else return true
        } else return true
      }
    })
    this.$validator.extend('claims_Date_lossDate', {
      getMessage(field, args) {
        return `The ${field} cannot be earlier than Loss Date.`
      },
      validate(value, args) {
        if (value) {
          if (args === void 0) args = []
          var lossDate = args[0]
          if (lossDate) {
            return !isAfter(removeTime(lossDate), removeTime(value))
          } else return true
        } else return true
      }
    })
    this.$validator.extend('claims_Date_today', {
      getMessage(field, args) {
        return `The ${field} cannot be later than Today.`
      },
      validate(value, args) {
        if (value) {
          return !isAfter(removeTime(value), removeTime(new Date()))
        } else return true
      }
    })
    this.$validator.extend('tranDateRangeValidation', {
      getMessage(field, args) {
        return `Not permitted in Transaction Date Range (xTranDateRange)`
      },
      validate(value, args) {
        if (value) {
          var fromDate = args[0]
          var toDate = args[1]
          var tranDate = args[2]

          if (isAfter(removeTime(fromDate), removeTime(tranDate)) || !isAfter(removeTime(toDate), removeTime(tranDate))) {
            return false
          } else {
            return true
          }
        }
      }
    })
  },
  mixins: [MessageMixin, TableConst.default],
  components: {
    ProcessNotification
  }
}
</script>

<style lang="scss">
.lossDateWarning {
  .v-messages__message {
    color: $error !important;
  }
}

.hasClaimWarning {
  .v-messages__message {
    color: $error !important;
  }
}
</style>
